import React, { useState } from 'react';
import { 
  Search, Book, MessageCircle, Mail, Phone, FileText, Video, ExternalLink,
  ChevronRight, HelpCircle, Zap, Database, Shield, Code, Clock, CheckCircle, Send
} from 'lucide-react';

const HelpSupport = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'medium'
  });

  const quickLinks = [
    { title: 'Getting Started', description: 'Learn the basics of VectorMigrate', icon: Zap, color: 'text-blue-400', bgColor: 'bg-blue-500/20', link: '#' },
    { title: 'Migration Guide', description: 'Step-by-step migration tutorials', icon: Database, color: 'text-purple-400', bgColor: 'bg-purple-500/20', link: '#' },
    { title: 'API Documentation', description: 'Complete API reference', icon: Code, color: 'text-emerald-400', bgColor: 'bg-emerald-500/20', link: '#' },
    { title: 'Security & Privacy', description: 'Data protection guidelines', icon: Shield, color: 'text-amber-400', bgColor: 'bg-amber-500/20', link: '#' }
  ];

  const faqCategories = [
    { id: 'all', label: 'All Topics' },
    { id: 'general', label: 'General' },
    { id: 'migration', label: 'Migration' },
    { id: 'billing', label: 'Billing' },
    { id: 'technical', label: 'Technical' },
    { id: 'security', label: 'Security' }
  ];

  const faqs = [
    { category: 'general', question: 'What is VectorMigrate?', answer: 'VectorMigrate is a comprehensive platform for migrating vector databases seamlessly.' },
    { category: 'general', question: 'Which vector databases are supported?', answer: 'ChromaDB, Pinecone, Qdrant, and Weaviate.' },
    { category: 'migration', question: 'How long does migration take?', answer: 'Depends on dataset size — from minutes to hours.' },
    { category: 'billing', question: 'What are pricing plans?', answer: 'Free, Pro, and Enterprise plans are available.' },
    { category: 'technical', question: 'How do I connect my DB?', answer: 'Use the integration wizard in dashboard.' },
    { category: 'security', question: 'How is my data secured?', answer: 'We use AES-256 encryption.' }
  ];

  const filteredFaqs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(f => f.category === selectedCategory);

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="p-2">
        <h1 className="text-3xl font-bold text-white mb-2">Help & Support</h1>
        <p className="text-gray-400">Find answers, guides & assistance</p>
      </div>

      {/* Search */}
      {/* <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
        <div className="relative max-w-3xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
          <input
            type="text"
            placeholder="Search articles, guides, FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white"
          />
        </div>
      </div> */}

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickLinks.map((link, i) => {
          const Icon = link.icon;
          return (
            <a
              key={i}
              href={link.link}
              className="bg-[#141414] border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition"
            >
              <div className={`w-12 h-12 ${link.bgColor} rounded-lg flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 ${link.color}`} />
              </div>
              <h3 className="text-white font-semibold mb-1">{link.title}</h3>
              <p className="text-gray-400 text-sm mb-3">{link.description}</p>
              <div className="flex items-center text-blue-400 text-sm">
                Learn more <ChevronRight className="ml-1 w-4 h-4" />
              </div>
            </a>
          );
        })}
      </div>

      {/* FAQs */}
      <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
        <div className="flex gap-2 items-center mb-6">
          <HelpCircle className="w-6 h-6 text-blue-400" />
          <h2 className="text-xl font-bold text-white">FAQs</h2>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          {faqCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-lg ${
                selectedCategory === cat.id 
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {filteredFaqs.map((faq, i) => (
            <details key={i} className="bg-gray-900 border border-gray-800 rounded-lg">
              <summary className="cursor-pointer px-6 py-4 flex justify-between items-center">
                <span className="text-white">{faq.question}</span>
                <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="px-6 py-4 border-t border-gray-800 text-gray-400 text-sm">
                {faq.answer}
              </div>
            </details>
          ))}
        </div>
      </div>

      {/* Status Banner */}
      <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-xl p-6">
        <div className="flex gap-4">
          <CheckCircle className="text-emerald-400 w-6 h-6" />
          <div>
            <h3 className="text-emerald-400 font-semibold">All Systems Operational</h3>
            <p className="text-gray-400 text-sm mb-2">
              All services are running normally.
            </p>
            <a
              href="#"
              className="inline-flex items-center text-emerald-400 text-sm hover:text-emerald-300"
            >
              View Status Page
              <ExternalLink className="w-4 h-4 ml-1" />
            </a>
          </div>
        </div>
      </div>

      <footer className="text-center text-sm text-gray-500 py-4">
        © 2024 VectorMigrate. All rights reserved.
      </footer>
    </div>
  );
};

export default HelpSupport;
